import { useCallback, useEffect, useRef, useState } from "react";
import type { NavigateFunction } from "react-router-dom";

import {
  getPresetFiles,
  getProviderSelectionFromProvider,
  providerSelectionToApiValue,
  providerSelectionToPreset,
  type SkillCollectionProviderSelection,
} from "../../skill-presets";
import { buildProviderReviewItem } from "../../utils/review-items";
import { migrateProvider } from "../../utils/provider-migration";
import type { SkillCollectionEditorStore, SkillCollectionReviewItem } from "../../store";

import type { useSaveSkillCollectionVersion } from "@/queries/skill-collections";

interface UseEditorSaveReviewParams {
  store: SkillCollectionEditorStore;
  provider?: string | null;
  rootPath?: string | null;
  saveMutation: ReturnType<typeof useSaveSkillCollectionVersion>;
  workspaceId: number;
  collectionId: string;
  navigate: NavigateFunction;
}

export type SkillCollectionSavePhase = "review" | "saving" | "success";

const SUCCESS_REDIRECT_DELAY_MS = 900;

export const useEditorSaveReview = ({
  store,
  provider,
  rootPath,
  saveMutation,
  workspaceId,
  collectionId,
  navigate,
}: UseEditorSaveReviewParams) => {
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewSnapshot, setReviewSnapshot] = useState<SkillCollectionReviewItem[]>([]);
  const [pendingProvider, setPendingProvider] = useState<
    SkillCollectionProviderSelection | undefined
  >(undefined);
  const [savePhase, setSavePhase] = useState<SkillCollectionSavePhase>("review");
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentProviderSelection = getProviderSelectionFromProvider(
    provider as string | null | undefined,
  );
  const effectiveProvider = pendingProvider ?? currentProviderSelection;
  const hasProviderChange =
    pendingProvider !== undefined && pendingProvider !== currentProviderSelection;

  useEffect(
    () => () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    },
    [],
  );

  const resetSavePhase = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    setSavePhase("review");
  }, []);

  const handleProviderChange = useCallback(
    (selection: SkillCollectionProviderSelection) => {
      const oldProvider = providerSelectionToPreset(effectiveProvider);
      const nextProvider = providerSelectionToPreset(selection);
      setPendingProvider(selection);

      if (store.isEmpty) {
        if (nextProvider) {
          store.applyPresetFiles(getPresetFiles(nextProvider), rootPath);
        }
        return;
      }

      migrateProvider(oldProvider, nextProvider, store);
    },
    [effectiveProvider, rootPath, store],
  );

  const handleOpenSaveReview = useCallback(() => {
    const { fileUpdates, moves, deletes } = store.savePayload;

    if (
      fileUpdates.length === 0 &&
      moves.length === 0 &&
      deletes.length === 0 &&
      !hasProviderChange
    ) {
      return;
    }

    const providerReviewItem = buildProviderReviewItem(
      providerSelectionToApiValue(currentProviderSelection),
      providerSelectionToApiValue(effectiveProvider),
    );
    setReviewSnapshot(
      providerReviewItem ? [providerReviewItem, ...store.reviewItems] : store.reviewItems,
    );
    resetSavePhase();
    setIsReviewDialogOpen(true);
  }, [currentProviderSelection, effectiveProvider, hasProviderChange, resetSavePhase, store]);

  const handleCloseReviewDialog = useCallback(() => {
    if (savePhase !== "review") {
      return;
    }

    resetSavePhase();
    setIsReviewDialogOpen(false);
  }, [resetSavePhase, savePhase]);

  const handleConfirmSave = useCallback(
    (commitMessage?: string) => {
      const { fileUpdates, moves, deletes } = store.savePayload;
      setSavePhase("saving");

      saveMutation.mutate(
        {
          workspaceId,
          collectionId,
          fileUpdates,
          moves,
          deletes,
          commitMessage,
          provider: providerSelectionToApiValue(pendingProvider),
        },
        {
          onSuccess: response => {
            store.markSaveSuccess();
            setSavePhase("success");
            successTimeoutRef.current = setTimeout(() => {
              setIsReviewDialogOpen(false);
              resetSavePhase();
              navigate(
                `/workspace/${workspaceId}/skill-collection/${collectionId}/version/${response.version.number}`,
                { replace: true },
              );
            }, SUCCESS_REDIRECT_DELAY_MS);
          },
          onError: () => {
            resetSavePhase();
          },
        },
      );
    },
    [collectionId, navigate, pendingProvider, resetSavePhase, saveMutation, store, workspaceId],
  );

  return {
    isReviewDialogOpen,
    savePhase,
    reviewSnapshot,
    effectiveProvider,
    pendingProvider,
    hasProviderChange,
    handleProviderChange,
    handleOpenSaveReview,
    handleCloseReviewDialog,
    handleConfirmSave,
  };
};
